import { AppBar, Box, Button, Toolbar, Typography, Select, MenuItem, Stack } from "@mui/material";
import { selectUser, UserInfo } from "../../../redux/user/userSlice";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../../app/hooks";
import { routineSlice, retrieveRoutine, selectRoutine } from "../../../redux/routine/routineSlice";
import { useNavigate } from "react-router";
import { useParams } from "react-router-dom";
import { retrieveSchool, schoolSlice, selectSchool } from "../../../redux/school/schoolSlice";
import { ContextMenu, ContextMenuOptions } from "../../../features/base/ContextMenu";
import RoutinePageAvatar from "../../routine/basics/RoutinePageAvatar";
import PageAuthenticatedLoading from "../../../features/pages/PageAuthenticatedLoading";
import MenuIcon from "@mui/icons-material/Menu";
import { isTokenReady } from "../../../features/user/UserAvatar";
import { PermissionId, anyPermission } from "../../../app/permissions";
import { routeRoutineFeed } from "../../../routes/routine";
import { routeSchool } from "../../../routes/school";
import { ElementMissingContext } from "../../../features/base/ElementMissingContext";
import { TopBar } from "../../../features/top/TopBar";
import { useEffect, useState } from "react";

import dayjs from "dayjs";
import { retrieveRoutineItemList, routineItemListSlice, selectRoutineItemList } from "../../../redux/routine/routineItemListSlice";
import { retrieveRoutineItemOptionList, routineItemOptionListSlice, selectRoutineItemOptionList } from "../../../redux/routine/routineItemOptionListSlice";
import { retrieveRoutineRecordList, routineRecordListSlice, selectRoutineRecordList } from "../../../redux/routine/routineRecordListSlice";
import { classroomStudentsSlice, retrieveClassroomStudents, selectClassroomStudents } from "../../../redux/classroom/classroomStudentsSlice";
import { classroomSlice, retrieveClassroom, selectClassroom } from "../../../redux/classroom/classroomSlice";
import { routeClassroom } from "../../../routes/classroom";
import { DatePicker } from "@mui/x-date-pickers";
import ClassroomPageAvatar from "../basics/ClassroomPageAvatar";
import ClassroomPageTitle from "../basics/ClassroomPageTitle";
import BadgedAvatar from "../../../features/base/BadgedAvatar";
import RoutineTableContainer from "./RoutineTableContainer";
import { assertThumbnailUrl } from "../../../redux/common";
import { retrieveRoutineCommentList, routineCommentListSlice, selectRoutineCommentList } from "../../../redux/routine/routineCommentListSlice";

export function isToday(date: dayjs.Dayjs): boolean {
    const now = dayjs();
    const ret = date.year() === now.year() && date.month() === now.month() && date.date() === now.date();
    // console.log(`isToday :: ${date.format("YYYY-MM-DD")} / ${now.format("YYYY-MM-DD")} => ${ret}`);
    return ret;
}

export function hasPermissionToEditRoutine(selectedDate: dayjs.Dayjs, user: UserInfo | undefined, classroomId: string | undefined, schoolId: string | undefined): boolean {
    return (
        ( 
            isToday(selectedDate) 
            && anyPermission(user, classroomId, [PermissionId.CLASSROOM_ADMIN, PermissionId.CLASSROOM_TEACHER])
        )
        || anyPermission(user, schoolId, [PermissionId.SCHOOL_ADMIN])
    );
}

export function RoutineFeed(/* props: RoutineFeedProps */) {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const user = useSelector(selectUser);
    const routineItems = useSelector(selectRoutineItemList);
    const routineItemOptions = useSelector(selectRoutineItemOptionList);
    const routineRecords = useSelector(selectRoutineRecordList);
    const routineComments = useSelector(selectRoutineCommentList);
    const classroomStudents = useSelector(selectClassroomStudents);

    const school = useSelector(selectSchool);
    const routine = useSelector(selectRoutine);
    const classroom = useSelector(selectClassroom);

    const { schoolId, classroomId, routineId, eventDate } = useParams();
    const [selectedDate, setSelectedDate] = useState(dayjs(eventDate) || dayjs());
    const [selectedRoutineItem, setSelectedRoutineItem] = useState<string>("");

    const canEdit = hasPermissionToEditRoutine(selectedDate, user?.priza?.info, classroomId, schoolId);

    const cleanup = () => {
        dispatch(schoolSlice.actions.clean());
        dispatch(classroomSlice.actions.clean());
        dispatch(routineSlice.actions.clean());
        dispatch(routineItemListSlice.actions.clean());
        dispatch(routineItemOptionListSlice.actions.clean());
        dispatch(routineRecordListSlice.actions.clean());
        dispatch(routineCommentListSlice.actions.clean());
        dispatch(classroomStudentsSlice.actions.clean());
    };

    useEffect(() => {
        if (isTokenReady(user) && classroomId) {
            classroomSlice.actions.check(classroomId);
            dispatch(retrieveClassroom({ classroomId }));
        }
    }, [
        user, classroomId, classroom
    ]);

    useEffect(() => {
        const classroomSchoolId = `${classroom.selected?.schoolId}`;
        const classroomRoutineId = `${classroom.selected?.routineId}`;
        if(classroomId && classroom.selected) {
            if(classroomSchoolId !== schoolId) {
                console.debug(`REDIRECT :: school=${classroomSchoolId}/${schoolId}`);
                navigate(routeRoutineFeed(classroomSchoolId, classroomId, classroomRoutineId, selectedDate.format("YYYY-MM-DD")));
            }
            if(classroomRoutineId !== routineId) {
                console.debug(`REDIRECT :: routine ${classroomRoutineId}/${routineId}`);
                navigate(routeRoutineFeed(classroomSchoolId, classroomId, classroomRoutineId, selectedDate.format("YYYY-MM-DD")));
            }
        }
    }, [classroom.selected]);

    useEffect(() => {
        const classroomSchoolId = `${classroom.selected?.schoolId}`;
        const classroomRoutineId = `${classroom.selected?.routineId}`;
        if (isTokenReady(user) 
            && classroomId && classroom.selected
            && routineId 
            && schoolId
            && schoolId === classroomSchoolId
            && routineId === classroomRoutineId
        ) {
            classroomSlice.actions.check(classroomId);
            dispatch(retrieveClassroom({ classroomId }));
            schoolSlice.actions.check(schoolId);
            routineSlice.actions.check(routineId);
            routineItemListSlice.actions.check({routineId});
            routineItemOptionListSlice.actions.check({routineId});
            routineRecordListSlice.actions.check({ routineId, classroomId, eventDate: selectedDate.format("YYYY-MM-DD") });
            routineCommentListSlice.actions.check({ routineId, classroomId, eventDate: selectedDate.format("YYYY-MM-DD") });
            classroomStudentsSlice.actions.check({ schoolId, classroomId });

            dispatch(retrieveSchool({ schoolId }));
            if(canEdit) {
                dispatch(retrieveRoutine({ routineId }));
                dispatch(retrieveRoutineItemList({ routineId }));
                dispatch(retrieveRoutineItemOptionList({ routineId }));
                dispatch(retrieveRoutineRecordList({ routineId, classroomId, eventDate: selectedDate.format("YYYY-MM-DD") }));
                dispatch(retrieveRoutineCommentList({ routineId, classroomId, eventDate: selectedDate.format("YYYY-MM-DD") }));
                dispatch(retrieveClassroomStudents({ schoolId, classroomId }));
            }
        }
    }, [
        user, selectedDate, schoolId, classroomId, routineId,
        school, classroom, routine, 
        routineItems, routineItemOptions, routineRecords, routineComments,
        classroomStudents, canEdit
    ]);

    if ((!schoolId) || (!classroomId) || (!routineId)) {
        return <ElementMissingContext />;
    }

    const options: ContextMenuOptions[] = [];
    options.push({ label: "Atualizar", click: cleanup });
    const topMenu = <ContextMenu label={<MenuIcon />} options={options} />;

    const breadcrumbs = [
        {
            label: school.selected?.name ?? "Escola",
            href: routeSchool(schoolId, "routine")
        },
        {
            label: routine.selected?.name || "Rotina",
        },
    ];


    let routineItemsToPresent = routineItems.list || [];
    routineItemsToPresent = [...routineItemsToPresent];
    routineItemsToPresent.push({
        id: "comment",
        name: "Observação",
        url: "",
        routineId: routineId || "",
    });
    routineItemsToPresent = selectedRoutineItem
        ? routineItemsToPresent.filter(item => item.id === selectedRoutineItem) || []
        : routineItemsToPresent.sort((a, b) => (a.order || 999) - (b.order || 999));


    return <PageAuthenticatedLoading
        topbar={<TopBar
            topMenu={topMenu}
            title={
                <Typography variant="h6" component="div">
                    Rotina
                </Typography>
            }
            breadcrumbs={breadcrumbs}
        />}
        loading={{
            states: [
                school.state,
                classroom.state,
                routine.state,
                routineItems.state, 
                routineItemOptions.state, 
                routineRecords.state, 
                routineComments.state,
                classroomStudents.state
            ],
            content: () => {
                const body = () => {
                    if(canEdit === false) {
                        return <Typography variant="h6" component="div" sx={{ m: 2 }}>
                            Você não tem permissão para ver esta rotina.
                        </Typography>;
                    }
                    return (
                        <RoutineTableContainer
                            classroomId={classroomId}
                            eventDate={selectedDate.format("YYYY-MM-DD")}
                            classroomStudents={classroomStudents.list || []}
                            routineItems={routineItemsToPresent}
                            routineItemOptions={routineItemOptions.list || []}
                            routineRecords={routineRecords.list || []}
                            routineComments={routineComments.list || []}
                        />
                    );
                };

                return <>
                    <AppBar key={"app-name"} id={"app-name"} position="static" color="default">
                        <Toolbar>
                            <ClassroomPageAvatar />
                            <ClassroomPageTitle schoolId={schoolId} classroomId={classroomId} />
                            <Box sx={{ flexGrow: 1 }} />
                            <Button onClick={() => navigate(routeClassroom(schoolId, classroomId, "routine"))}>Voltar</Button>
                        </Toolbar>
                    </AppBar>
                    <AppBar key={"app-control"} id={"app-control"} position="static" color="primary" elevation={2}>
                        <Toolbar>
                            <RoutinePageAvatar />
                            <Typography variant="h6" component="div" sx={{ pr: 2 }}>
                                {routine.selected?.name}
                            </Typography>
                            <DatePicker
                                value={selectedDate}
                                onChange={(newValue) => {
                                    if (newValue) setSelectedDate(newValue);
                                    dispatch(routineRecordListSlice.actions.clean());
                                    dispatch(routineCommentListSlice.actions.clean());
                                }}
                                format="DD/MM/YYYY"
                                slotProps={{ textField: { size: "small", sx: { width: "150px", backgroundColor: "white" } } }}
                            />
                            <Select
                                value={selectedRoutineItem}
                                onChange={(e) => setSelectedRoutineItem(e.target.value)}
                                displayEmpty
                                size="small"
                                sx={{ ml: 2, minWidth: 180, backgroundColor: "white" }}
                            >
                                <MenuItem value="">
                                    <em>Todos</em>
                                </MenuItem>
                                {[...(routineItems.list || [])]
                                .sort((a, b) => (a.order || 999) - (b.order || 999))
                                .map(item => (
                                    <MenuItem key={item.id} value={item.id}>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <BadgedAvatar
                                                avatar={assertThumbnailUrl(item.url)}
                                                title={item.name}
                                                size="24px"
                                                fontSize="small"
                                            />
                                            <Typography variant="body2">{item.name}</Typography>
                                        </Stack>
                                    </MenuItem>
                                ))}
                                <MenuItem value="comment">
                                    Observação
                                </MenuItem>
                            </Select>
                        </Toolbar>
                    </AppBar>
                    {body()}
                </>;
            },
            error: "Erro ao carregar lista de rotinas",
            retry: cleanup
        }} />;
}
